import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { currentRoundOf, selectGameList, useScorekeeper } from '@/store/scorekeeper';
import { selectKlaverjasList, useKlaverjasScore } from '@/store/klaverjasScore';
import { klaverjasTotals } from '@shared/types/klaverjas';
import { standings } from '@shared/game-logic';
import { Panel } from '@/components/ui';
import FeedbackButton from '@/components/FeedbackButton';
import ThemePicker from '@/components/ThemePicker';
import { formatDateTime } from '@/lib/labels';
import { describeRoom, useLiveResume } from '@/live/useLiveResume';
import ResumeFromServer from '@/components/ResumeFromServer';
import DeleteButton from '@/components/DeleteButton';
import { removeGame } from '@/lib/removeGame';
import { forgetSeat } from '@/live/sessions';

const LIVE_LABEL: Record<string, string> = {
  live: 'Boerenbridge live',
  hearts: 'Hartenjagen live',
  klaverjas: 'Klaverjassen live',
};

/**
 * The front door.
 *
 * Grouped by game, not by mode. With one game a flat list of buttons was the
 * whole menu; with three it stopped saying which game each button belonged to —
 * "Score bijhouden" and "Live spelen" were both boerenbridge and neither said
 * so. One card per game, with the ways to play it inside, keeps that obvious
 * however many games there end up being.
 */
export default function Home() {
  const navigate = useNavigate();
  const [removeError, setRemoveError] = useState<string | null>(null);

  const bridgeGames = useScorekeeper(selectGameList);
  const klaverjasGames = useKlaverjasScore(selectKlaverjasList);
  // Live tables this device still has a chair at. The server keeps the game; this
  // is the way back to it without remembering the code.
  const liveRooms = useLiveResume();

  const running = [
    ...liveRooms.map((room) => ({
      id: `live-${room.code}`,
      kind: 'live' as const,
      code: room.code,
      updatedAt: room.savedAt,
      game: LIVE_LABEL[room.mode] ?? 'Live',
      who: describeRoom(room),
      detail: `Code ${room.code}`,
      to: room.to,
    })),
    ...bridgeGames
      .filter((game) => game.status === 'in-progress')
      .map((game) => {
        const round = currentRoundOf(game);
        const leader = standings(game.rounds, game.seats)[0];
        return {
          id: game.id,
          kind: 'boerenbridge' as const,
          code: null,
          updatedAt: game.updatedAt,
          game: 'Boerenbridge',
          who: game.seats.map((seat) => seat.displayName).join(', '),
          detail: round
            ? `Ronde ${round.roundNo} van ${game.rounds.length} · ${round.cards} ${
                round.cards === 1 ? 'kaart' : 'kaarten'
              }${leader && leader.total !== 0 ? ` · ${leader.displayName} leidt` : ''}`
            : 'Klaar om af te ronden',
          to: `/score/${game.id}`,
        };
      }),
    ...klaverjasGames
      .filter((game) => game.status === 'in-progress')
      .map((game) => {
        const totals = klaverjasTotals(game);
        const played = game.deals.filter((deal) => deal.result).length;
        return {
          id: game.id,
          kind: 'klaverjas' as const,
          code: null,
          updatedAt: game.updatedAt,
          game: 'Klaverjassen',
          who: `${game.seats[0]?.displayName} & ${game.seats[2]?.displayName} — ${game.seats[1]?.displayName} & ${game.seats[3]?.displayName}`,
          detail: `Gift ${played + 1} · ${totals[0]} — ${totals[1]}`,
          to: `/klaverjas/${game.id}`,
        };
      }),
  ].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

  const lastPlayed = [...bridgeGames, ...klaverjasGames].sort((a, b) =>
    b.updatedAt.localeCompare(a.updatedAt)
  )[0];

  return (
    <div className="min-h-dvh text-white">
      {/* Home has its own header rather than Screen, so the mute control is
          placed explicitly here. */}
      <header className="flex items-start justify-between gap-3 px-5 pb-2 pt-10">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Kaarten</h1>
          <p className="mt-1 text-slate-400">Drie spellen, aan tafel of op de telefoon</p>
        </div>
        <FeedbackButton />
      </header>

      <div className="space-y-4 p-4">
        {running.length > 0 && (
          <Panel title="Verder spelen">
            <ul className="space-y-2">
              {running.map((game) => (
                <li key={game.id} className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigate(game.to)}
                    className="min-w-0 flex-1 rounded-xl bg-base-700 px-4 py-3 text-left hover:bg-base-600"
                  >
                    <span className="block truncate font-medium">{game.who}</span>
                    <span className="block truncate text-sm text-slate-400">
                      {game.game} · {game.detail}
                    </span>
                  </button>

                  {/*
                    A live potje and a scorekeeper potje are different things to be
                    rid of. The first belongs to everyone at that table, so this
                    only forgets your seat here; the second is a document this
                    device owns, so it really goes.
                  */}
                  <DeleteButton
                    label={game.kind === 'live' ? 'weg' : 'weggooien'}
                    onDelete={async () => {
                      setRemoveError(null);
                      if (game.kind === 'live') {
                        if (game.code) forgetSeat(game.code);
                        return;
                      }
                      try {
                        await removeGame(game.id, game.kind);
                      } catch (err) {
                        setRemoveError(err instanceof Error ? err.message : 'Weggooien mislukt.');
                      }
                    }}
                  />
                </li>
              ))}
            </ul>
            {removeError && (
              <p role="alert" className="mt-3 text-sm text-bad">
                {removeError}
              </p>
            )}
            <p className="mt-3 text-xs text-slate-500">
              Een live potje weghalen doet het alleen van dit toestel — de anderen spelen door.
            </p>
          </Panel>
        )}

        {/*
          Potjes that live on another phone. Under "Verder spelen" rather than in
          Historie, because this is where you look when you want to carry on —
          and the two lists are deliberately separate: one is on this device, the
          other has to be taken over first.
        */}
        <ResumeFromServer known={[...bridgeGames, ...klaverjasGames].map((game) => game.id)} />

        <GameCard
          name="Boerenbridge"
          blurb="Voorspel je slagen en maak ze waar"
          accent="accent"
          score={{ to: '/score/nieuw', label: 'Score bijhouden' }}
          live={{ to: '/live', label: 'Live spelen' }}
          rules="/regels"
        />

        <GameCard
          name="Klaverjassen"
          blurb="Twee tegen twee · roem, nat en pit"
          accent="amber"
          score={{ to: '/klaverjas/nieuw', label: 'Score bijhouden' }}
          live={{ to: '/klaverjas/live', label: 'Live spelen' }}
          rules="/regels/klaverjassen"
        />

        <GameCard
          name="Hartenjagen"
          blurb="Met z'n vieren · de laagste score wint"
          accent="rose"
          live={{ to: '/harten', label: 'Live spelen' }}
          rules="/regels/hartenjagen"
        />

        <Link
          to="/historie"
          className="themed-panel flex items-center justify-center gap-2 bg-base-800/60 px-4 py-4 text-center hover:bg-base-700/60"
        >
          <span className="text-xl">🏆</span>
          <span className="font-medium">Historie en ranglijst</span>
        </Link>

        <Panel>
          <ThemePicker />
        </Panel>

        {lastPlayed && (
          <p className="pt-2 text-center text-sm text-slate-500">
            Laatst gespeeld {formatDateTime(lastPlayed.updatedAt)}
          </p>
        )}
      </div>
    </div>
  );
}

/*
 * Tailwind scans for whole class names, so the accent cannot be built by
 * interpolation — a `border-${accent}-500/40` would compile to nothing and the
 * card would silently lose its colour.
 */
const ACCENTS = {
  accent: {
    // Only the tint here: the outline width and colour come from the theme, so
    // `speels` gets its heavy dark line and `nu` its hairline without either
    // game card knowing which is on.
    card: 'bg-accent-500/5',
    primary: 'bg-accent-500 text-base-950 hover:bg-accent-400',
    secondary: 'border border-accent-500/40 text-accent-100 hover:bg-accent-500/15',
  },
  amber: {
    card: 'bg-amber-400/10',
    primary: 'bg-amber-400 text-base-950 hover:bg-amber-300',
    secondary: 'border border-amber-400/40 text-amber-100 hover:bg-amber-400/15',
  },
  rose: {
    card: 'bg-rose-400/10',
    primary: 'bg-rose-400 text-base-950 hover:bg-rose-300',
    secondary: 'border border-rose-400/40 text-rose-100 hover:bg-rose-400/15',
  },
} as const;

interface Way {
  to: string;
  label: string;
}

function GameCard({
  name,
  blurb,
  accent,
  score,
  live,
  rules,
}: {
  name: string;
  blurb: string;
  accent: keyof typeof ACCENTS;
  /** Not every game has both ways to play it. */
  score?: Way;
  live?: Way;
  rules: string;
}) {
  const style = ACCENTS[accent];

  return (
    <section className={`themed-panel p-4 ${style.card}`}>
      <header className="mb-3 flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">{name}</h2>
          <p className="text-sm text-slate-400">{blurb}</p>
        </div>
        <Link
          to={rules}
          className="shrink-0 text-sm text-slate-500 underline underline-offset-4 hover:text-slate-300"
        >
          regels
        </Link>
      </header>

      <div className={`grid gap-2 ${score && live ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {score && (
          <Way to={score.to} className={style.primary}>
            {score.label}
          </Way>
        )}
        {live && (
          <Way to={live.to} className={score ? style.secondary : style.primary}>
            {live.label}
          </Way>
        )}
      </div>
    </section>
  );
}

function Way({ to, className, children }: { to: string; className: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className={`themed-control flex min-h-12 items-center justify-center px-3 text-center text-sm font-medium transition-colors ${className}`}
    >
      {children}
    </Link>
  );
}
